import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, ArrowRight, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { CapturedFrame } from './CameraStep';
interface QualityCheckStepProps {
  captures: CapturedFrame[];
  onContinue: () => void;
  onRescan: () => void;
}
export function QualityCheckStep({
  captures,
  onContinue,
  onRescan
}: QualityCheckStepProps) {
  const {
    t
  } = useLanguage();
  // Heuristic quality scoring — based on whether all angles have data
  const {
    score,
    checks
  } = useMemo(() => {
    const front = captures.find(c => c.angle === 'front');
    const hasLandmarks = !!front && front.landmarks.length === 68;
    // Each criterion: 0-100
    const clarity = hasLandmarks ? 88 : 72;
    const lighting = hasLandmarks ? 85 : 70;
    const angle = captures.length === 3 ? 92 : 65;
    const distance = hasLandmarks ? 90 : 75;
    const score = Math.round((clarity + lighting + angle + distance) / 4);
    return {
      score,
      checks: [{
        key: 'clarity',
        label: t('faceAnalysis.quality.clarity'),
        value: clarity
      }, {
        key: 'lighting',
        label: t('faceAnalysis.quality.lighting'),
        value: lighting
      }, {
        key: 'angle',
        label: t('faceAnalysis.quality.angle'),
        value: angle
      }, {
        key: 'distance',
        label: t('faceAnalysis.quality.distance'),
        value: distance
      }]
    };
  }, [captures, t]);
  // Circle progress geometry
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - score / 100 * circumference;
  const angleLabel = (a: string) => a === 'front' ? t('faceAnalysis.camera.angleFront') : a === 'angle45' ? t('faceAnalysis.camera.angle45') : t('faceAnalysis.camera.angleSide');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl shadow-brand-copper/5 border border-brand-lightCopper/30 overflow-hidden" data-id="element-366">
      <div className="p-6 sm:p-10" data-id="element-367">
        <div className="text-center mb-8" data-id="element-368">
          <div className="w-14 h-14 bg-brand-copper/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-369">
            <ShieldCheck className="w-7 h-7 text-brand-copper" data-id="element-370" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-light text-gray-900 mb-2" data-id="element-371">
            {t('faceAnalysis.quality.title')}
          </h2>
          <p className="text-gray-600 text-sm sm:text-base" data-id="element-372">
            {t('faceAnalysis.quality.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-8" data-id="element-373">
          {/* Circular score */}
          <div className="flex flex-col items-center" data-id="element-374">
            <div className="relative w-44 h-44" data-id="element-375">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160" data-id="element-376">
                <circle cx="80" cy="80" r={radius} fill="none" stroke="#f3f4f6" strokeWidth="10" data-id="element-377" />
                <motion.circle cx="80" cy="80" r={radius} fill="none" stroke="#c79268" strokeWidth="10" strokeLinecap="round" initial={{
                strokeDashoffset: circumference
              }} animate={{
                strokeDashoffset: offset
              }} transition={{
                duration: 1.2,
                ease: 'easeOut'
              }} style={{
                strokeDasharray: circumference
              }} data-id="element-378" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-379">
                <motion.div initial={{
                opacity: 0,
                scale: 0.5
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.5
              }} className="text-4xl font-light text-gray-900" data-id="element-380">
                  {score}%
                </motion.div>
                <div className="text-xs text-gray-500 tracking-wider uppercase mt-1" data-id="element-381">
                  {t('faceAnalysis.quality.score')}
                </div>
              </div>
            </div>
          </div>

          {/* Checks */}
          <div className="space-y-3" data-id="element-382">
            {checks.map((c, i) => <motion.div key={c.key} initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3 + i * 0.1
          }} className="flex items-center justify-between" data-id="element-383">
                <div className="flex items-center text-gray-700" data-id="element-384">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" data-id="element-385" />
                  <span className="text-sm font-medium" data-id="element-386">{c.label}</span>
                </div>
                <span className="text-sm text-gray-500 font-medium" data-id="element-387">
                  {c.value}%
                </span>
              </motion.div>)}
          </div>
        </div>

        {/* Thumbnails */}
        <div className="flex justify-center gap-3 mb-8" data-id="element-388">
          {captures.map(c => <div key={c.angle} className="text-center" data-id="element-389">
              <div className="w-20 h-24 sm:w-24 sm:h-28 rounded-xl overflow-hidden border-2 border-green-400 mb-1.5 relative" data-id="element-390">
                <img src={c.imageSrc} alt={c.angle} className="w-full h-full object-cover" style={{
              transform: 'scaleX(-1)'
            }} data-id="element-391" />
                <div className="absolute bottom-1 right-1 bg-green-500 rounded-full p-0.5 shadow" data-id="element-392">
                  <CheckCircle2 className="w-3 h-3 text-white" data-id="element-393" />
                </div>
              </div>
              <span className="text-xs text-gray-600 font-medium" data-id="element-394">
                {angleLabel(c.angle)}
              </span>
            </div>)}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-395">
          <button onClick={onRescan} className="flex items-center justify-center px-6 py-3 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-396">
            <RefreshCw className="w-4 h-4 mr-2" data-id="element-397" />
            {t('faceAnalysis.quality.rescan')}
          </button>
          <button onClick={onContinue} className="flex items-center justify-center px-8 py-3 rounded-full bg-brand-copper text-white hover:bg-brand-darkCopper transition-colors font-medium shadow-lg shadow-brand-copper/20" data-id="element-398">
            {t('faceAnalysis.quality.continue')}
            <ArrowRight className="w-4 h-4 ml-2" data-id="element-399" />
          </button>
        </div>
      </div>
    </motion.div>;
}